import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import Layout from '@/components/Layout.vue';
import { ROUTES } from '@/config/constants';
import { canAccessBudgetAdjustment } from '@/utils/budgetAdjustPermission';
import { getUserRole } from '@/utils/userInfo';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/components/Login.vue'),
  },
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard',
    children: [
      // Dashboard
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/components/pages/dashboard/Dashboard.vue'),
        meta: { requiresAuth: true },
      },
      // 在庫確認関連
      // {
      //   path: ROUTES.INVENTORY,
      //   name: 'Inventory',
      //   component: () => import('@/components/pages/inventory/MngInventory.vue'),
      //   meta: { requiresAuth: true },
      // },
      {
        path: ROUTES.INVENTORY_HISTORY,
        name: 'InventoryHistory',
        component: () => import('@/components/pages/inventory/InventoryHistory.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: ROUTES.ARRIVAL_STATUS,
        name: 'ArrivalStatus',
        component: () => import('@/components/pages/inventory/ArrivalStatus.vue'),
        meta: { requiresAuth: true },
      },
      // 申請可能数確認（一般用户、数据来自 USAGE + USAGE_LIMIT）
      {
        path: ROUTES.USAGE_CONFIRMATION,
        name: 'UsageConfirmation',
        component: () => import('@/components/pages/usageConfirmation/UsageConfirmation.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: ROUTES.DECREASE_DELETE_REQUEST,
        name: 'DecreaseDeleteRequest',
        component: () => import('@/components/pages/decreaseDeletion/DecreaseDeleteRequest.vue'),
        meta: { requiresAuth: true, allowedRoles: ['system', 'manager', 'user'] },
      },
      // 申請・受入関係
      {
        path: ROUTES.REQUEST_LIST,
        name: 'RequestList',
        component: () => import('@/components/pages/request/RequestList.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: ROUTES.DECREASE_DELETE_REQUEST_LIST,
        name: 'DecreaseDeleteRequestList',
        component: () => import('@/components/pages/decreaseDeletion/DecreaseDeleteRequestList.vue'),
        meta: { requiresAuth: true, allowedRoles: ['system', 'manager'] },
      },
      // 定数master判断业务（発注確認・不足確認）路由入口暂时注释
      // {
      //   path: ROUTES.REQUEST_REGISTER,
      //   name: 'RequestRegister',
      //   component: () => import('@/components/pages/request/RequestRegister.vue'),
      //   meta: { requiresAuth: true },
      // },
      {
        path: ROUTES.REQUEST_REGISTER_CONSUMPTION,
        name: 'RequestRegisterByConsumption',
        component: () => import('@/components/pages/request/RequestRegisterByConsumption.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: ROUTES.ORDER_CONFIRM_IN,
        name: 'OrderConfirmIn',
        component: () => import('@/components/pages/request/OrderConfirmIn.vue'),
        meta: { requiresAuth: true },
      },
      // 発注関連
      {
        path: ROUTES.REQUEST_LIST_SPD,
        name: 'RequestListSPD',
        component: () => import('@/components/pages/order/RequestListSPD.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: ROUTES.ORDER_REGISTER_OUT,
        name: 'OrderRegisterOut',
        component: () => import('@/components/pages/order/OrderRegisterOut.vue'),
        meta: { requiresAuth: true },
      },
      // 設定（Master表）
      {
        path: ROUTES.DEPARTMENT,
        name: 'Department',
        component: () => import('@/components/pages/settings/MngDepartment.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: ROUTES.ITEM,
        name: 'Item',
        component: () => import('@/components/pages/settings/MngItem.vue'),
        meta: { requiresAuth: true },
      },
      // 定数设定（定数マスタ）路由入口暂时注释
      // {
      //   path: ROUTES.CONSTANT,
      //   name: 'Constant',
      //   component: () => import('@/components/pages/settings/MngConstant.vue'),
      //   meta: { requiresAuth: true },
      // },
      {
        path: ROUTES.USAGE_LIMIT,
        name: 'UsageLimit',
        component: () => import('@/components/pages/settings/MngUsageLimit.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: ROUTES.USER,
        name: 'User',
        component: () => import('@/components/pages/settings/MngUser.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: ROUTES.MESSAGE,
        name: 'Message',
        component: () => import('@/components/pages/settings/MngMessage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: ROUTES.BUDGET_ADJUSTMENT,
        name: 'BudgetAdjustmentHistory',
        component: () => import('@/components/pages/settings/BudgetAdjustmentHistory.vue'),
        meta: { requiresAuth: true, requiresBudgetAdjustment: true },
      },
      // 環境設定（仅系统管理员、无数据表时仅搭建到 service 层）
      {
        path: ROUTES.ENV_SETTINGS,
        name: 'EnvSettings',
        component: () => import('@/components/pages/settings/EnvSettings.vue'),
        meta: { requiresAuth: true },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// 路由守卫
router.beforeEach((to, from, next) => {
  const token = localStorage.getItem('token');
  
  // 如果访问登录页且已登录，重定向到 dashboard
  if (to.path === `/${ROUTES.LOGIN}` && token) {
    next('/dashboard');
    return;
  }
  
  // 如果需要认证但没有 token，重定向到登录页
  if (to.meta.requiresAuth && !token) {
    next(`/${ROUTES.LOGIN}`);
    return;
  }

  if (to.meta.requiresBudgetAdjustment && !canAccessBudgetAdjustment()) {
    next(`/${ROUTES.FORBIDDEN}`);
    return;
  }

  const allowedRoles = to.meta.allowedRoles as string[] | undefined;
  if (allowedRoles && !allowedRoles.includes(getUserRole())) {
    next(`/${ROUTES.FORBIDDEN}`);
    return;
  }
  
  next();
});

export default router;

